import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Send, Mail, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-lark-ink placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-lark-blue focus:border-transparent transition-shadow duration-200";

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[28px] bg-lark-gray border border-gray-100 grid lg:grid-cols-2">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-lark-light blur-3xl opacity-70" />

          <div className="relative p-10 lg:p-14 flex flex-col justify-center">
            <Reveal>
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-lark-blue">
                Get in touch
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-lark-ink leading-[1.08]">
                Let's Build Better Ways of Working
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lark-muted leading-relaxed max-w-md">
                Tell us about your organization and goals. Our team will show you how Lark
                can transform the way your teams collaborate.
              </p>
            </Reveal>
            <div className="mt-9 space-y-4">
              <div className="flex items-center gap-3 text-sm text-lark-ink">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-white text-lark-blue border border-gray-100">
                  <Mail size={18} />
                </span>
                larksuite.finance@gmail.com
              </div>
              <div className="flex items-center gap-3 text-sm text-lark-ink">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-white text-lark-blue border border-gray-100">
                  <MapPin size={18} />
                </span>
                Singapore · Global Headquarters
              </div>
            </div>
          </div>

          <div className="relative p-8 lg:p-14">
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  className={field}
                  placeholder="Full name"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                  aria-label="Full name"
                />
                <input
                  className={field}
                  placeholder="Work email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email"
                  aria-label="Work email"
                />
              </div>
              <input
                className={field}
                placeholder="Company (optional)"
                value={form.company}
                onChange={update("company")}
                data-testid="contact-company"
                aria-label="Company"
              />
              <textarea
                className={`${field} min-h-[130px] resize-none`}
                placeholder="How can we help?"
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message"
                aria-label="Message"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-lark-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(22,119,255,0.30)] hover:bg-lark-blueDark disabled:opacity-70 transition-[background-color] duration-200"
              >
                {loading ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Contact Us
                    <Send size={16} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
